import {
    Box,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    TextField,
} from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import { isDefined } from '../../lib/utils';

type Option = {
    caption?: string;
    label?: string;
    value?: unknown;
    id?: unknown;
    key?: unknown;
};

type Props = {
    grid?: boolean;
    options?: Option[];
    defVal?: unknown;
    name?: string;
    cleanable?: boolean;
    caption?: string;
    onChange?: (value: { name: string | undefined; value: unknown }) => void;
    disabled?: boolean;
    mandatory?: boolean;
    translate?: boolean;
    required?: boolean;
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    [key: string]: unknown;
};

/**
 *    Form select
 *    ===========
 *
 * Props
 *
 *     * bool grid - wrap the element into a <Grid>
 *     * Array options - options as an array of {caption, value}
 *     * <any> defVal - the currently selected value
 *     * boolean cleanable: if it's true, a clean button (X) is added to reset the selection.
 *     * string name - the field name
 *     * string caption - the field caption (top label & placeholder text)
 *     * Function onChange(value) - callback when the selected value changes
 *     * bool disabled
 *     * bool mandatory - if mandatory, the value cannot be de-selected
 */
export function SttFormSelect({
    grid = false,
    options = [{ label: 'Empty', value: 0 }],
    defVal,
    name,
    cleanable = false,
    caption,
    onChange,
    disabled = false,
    mandatory = false,
    translate = true,
    required = false,
    xs,
    sm,
    md,
    lg,
    xl,
    ...rest
}: Props): React.ReactElement {
    const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange && onChange.constructor === Function) {
            onChange({ name, value: ev.target.value });
        }
    };

    // Los Select de material-ui tienen un bug por el cual si el 'value' de una opción no evalúa a true (por ej, 0, null, undefined, etc) el caption no se pinta al tener la opción seleccionada.
    const content = (
        <TextField
            fullWidth
            select
            name={name}
            variant="outlined"
            label={caption}
            size="small"
            value={isDefined(defVal) ? defVal : ''}
            onChange={handleChange}
            disabled={disabled}
            required={required}
            // Si el campo es select es 'mandatory' significa que no se puede de-seleccionar el valor
            // TODO: arreglar la 'x' para que funcione bien el hover
            InputProps={
                !mandatory && cleanable
                    ? {
                          endAdornment: (
                              <InputAdornment
                                  position="end"
                                  onClick={() =>
                                      onChange?.({ name, value: null })
                                  }
                              >
                                  {defVal && !disabled ? (
                                      <Box mr={1}>
                                          <IconButton>
                                              <ClearIcon fontSize="small" />
                                          </IconButton>
                                      </Box>
                                  ) : (
                                      <></>
                                  )}
                              </InputAdornment>
                          ),
                      }
                    : {}
            }
        >
            {/* JPB, 17 Abr 20. Se aceptan duplas value/cpation y id/label */}
            {options &&
                options.map((op, i) => (
                    <MenuItem
                        className={translate ? '' : 'notranslate'}
                        key={i}
                        value={
                            op.hasOwnProperty('value')
                                ? (op.value as any)
                                : op.hasOwnProperty('id')
                                  ? (op.id as any)
                                  : (op.key as any)
                        }
                    >
                        {op.caption || op.label}
                    </MenuItem>
                ))}
        </TextField>
    );

    const sizeProps = { xs, sm, md, lg, xl };
    return grid ? (
        <Grid size={sizeProps} {...rest}>
            {content}
        </Grid>
    ) : (
        content
    );
}
